/**
 * Payments — how this application gets paid for what it sells.
 *
 * The screen has one job beyond collecting keys: make the separation obvious.
 * Money taken here goes to the PROJECT OWNER's own merchant account, not to
 * Twasta — a different rail entirely from the platform subscription in
 * Settings → Billing. Everything on the Setup tab is about the shop's own
 * till.
 *
 * Two things the layout insists on, because both are where real setups fail:
 *   · the webhook URL is shown before the keys, with a copy button, because a
 *     gateway that cannot call back leaves orders paid at the bank and unpaid
 *     in the application;
 *   · "Test connection" makes a real API call, so wrong keys are found here
 *     rather than by the first customer.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  AlertTriangle, Check, Copy, CreditCard, Loader2, RefreshCw, RotateCcw,
  ShieldCheck, Trash2,
} from 'lucide-react'
import {
  deleteGateway, listGateways, listOrders, listWebhookEvents, refundOrder,
  retryWriteBack, saveGateway, setCashOnDelivery, setCollectPayments, testGateway,
  type GatewayListResponse, type GatewayProvider, type OrdersResponse,
  type PaymentGateway, type PaymentOrder, type WebhookEvent,
} from '@/api/appPayments'
// The form itself is shared with the in-app `payment_settings` block — see
// GatewaySetupForm. This screen supplies the PLATFORM endpoints for it.
import { blankGateway, GatewaySetupForm, type GatewaySetupApi } from './GatewaySetupForm'

/** Advanced → Payments talks to the builder's own, DEPLOY-gated router. */
const PLATFORM_GATEWAY_API: GatewaySetupApi = {
  save: saveGateway,
  test: testGateway,
  remove: deleteGateway,
  setCod: setCashOnDelivery,
}

const PROVIDER_LABEL: Record<string, string> = {
  razorpay: 'Razorpay',
  stripe: 'Stripe',
}

const STATUS_COLOR: Record<string, string> = {
  paid: 'var(--color-success, #16a34a)',
  created: 'var(--color-text-secondary)',
  failed: 'var(--color-error, #dc2626)',
  cancelled: 'var(--color-text-secondary)',
  refunded: 'var(--color-warning, #d97706)',
  partially_refunded: 'var(--color-warning, #d97706)',
}

function errText(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency })
      .format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

export default function PaymentsPage() {
  const { project = '' } = useParams<{ project: string }>()
  const navigate = useNavigate()

  const [data, setData] = useState<GatewayListResponse | null>(null)
  const [tab, setTab] = useState<'setup' | 'ledger'>('setup')
  const [provider, setProvider] = useState<GatewayProvider>('razorpay')
  const [loading, setLoading] = useState(true)
  const [collectBusy, setCollectBusy] = useState(false)

  const toggleCollect = async (enabled: boolean) => {
    setCollectBusy(true)
    try {
      const on = await setCollectPayments(project, enabled)
      setData(d => (d ? { ...d, collect_payments: on } : d))
      toast.success(on ? 'This application now collects payment'
        : 'Payment collection switched off')
    } catch (err) {
      toast.error(errText(err, 'Could not change the payment setting'))
    } finally {
      setCollectBusy(false)
    }
  }

  const load = useCallback(async () => {
    if (!project) return
    setLoading(true)
    try {
      setData(await listGateways(project))
    } catch (err) {
      toast.error(errText(err, 'Could not load this application’s payment setup'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void load() }, [load])

  const gateway = useMemo(() => {
    const found = data?.gateways.find(g => g.provider === provider)
    return found || blankGateway(provider)
  }, [data, provider])

  if (loading && !data) {
    return (
      <div style={{ padding: 40, display: 'flex', gap: 10, alignItems: 'center' }}>
        <Loader2 size={18} className="spin" /> Loading payment setup…
      </div>
    )
  }

  return (
    <div style={{ padding: '0 32px 32px', maxWidth: 940 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/workbench')}>
          &larr; Back
        </button>
        <h1 style={{ fontSize: 23, fontWeight: 600, margin: 0 }}>
          Payments: {project}
        </h1>
      </div>
      <p style={{
        color: 'var(--color-text-secondary)', fontSize: 14,
        margin: '0 0 20px 44px',
      }}>
        Connect the merchant account this application charges on. Money taken
        by the application settles to <strong>you</strong>, not to Twasta —
        this is separate from your own subscription. Until a gateway is
        connected and switched on, the application can still take cash on
        delivery or quote-only orders.
      </p>

      {/* The project switch. It is what makes the running application offer
          System administration → Payments to its owner / FUNC-ADMIN, so the
          people who run the shop can connect a gateway without the builder. */}
      <div className="card" style={{ padding: 16, margin: '0 0 18px 44px' }}>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
          cursor: collectBusy ? 'wait' : 'pointer' }}>
          <input type="checkbox" checked={!!data?.collect_payments}
                 disabled={collectBusy}
                 onChange={e => void toggleCollect(e.target.checked)} />
          <span style={{ fontSize: 14 }}>
            <strong>Collect payment in this application.</strong>{' '}
            <span style={{ color: 'var(--color-text-secondary)' }}>
              Adds a Payments tab to the running application&apos;s System
              administration, where its administrators can connect or change
              the gateway.
            </span>
          </span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18, marginLeft: 44 }}>
        {(['setup', 'ledger'] as const).map(key => (
          <button
            key={key}
            className={`btn btn-sm ${tab === key ? '' : 'btn-ghost'}`}
            onClick={() => setTab(key)}
          >
            {key === 'setup' ? 'Setup' : 'Orders & payouts'}
          </button>
        ))}
      </div>

      {tab === 'setup' ? (
        <GatewaySetupForm api={PLATFORM_GATEWAY_API}
          project={project}
          data={data}
          provider={provider}
          onProvider={setProvider}
          gateway={gateway}
          onSaved={load}
        />
      ) : (
        <LedgerTab project={project} />
      )}
    </div>
  )
}

// ── Setup ───────────────────────────────────────────────────────────────────

function LedgerTab({ project }: { project: string }) {
  const [data, setData] = useState<OrdersResponse | null>(null)
  const [events, setEvents] = useState<WebhookEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [orders, hooks] = await Promise.all([
        listOrders(project, { limit: 100 }),
        listWebhookEvents(project, 20).catch(() => [] as WebhookEvent[]),
      ])
      setData(orders)
      setEvents(hooks)
    } catch (err) {
      toast.error(errText(err, 'Could not load the payment ledger'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void load() }, [load])

  const onRefund = async (order: PaymentOrder) => {
    const raw = window.prompt(
      `Refund how much? (max ${order.amount - order.amount_refunded} ${order.currency})`,
      String(order.amount - order.amount_refunded))
    if (raw === null) return
    const amount = Number(raw)
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('That is not an amount')
      return
    }
    setBusyId(order.id)
    try {
      await refundOrder(project, order.id, amount, 'Refunded from the Payments screen')
      toast.success('Refund sent to the gateway')
      await load()
    } catch (err) {
      toast.error(errText(err, 'The refund was refused'))
    } finally {
      setBusyId('')
    }
  }

  const onRetry = async (order: PaymentOrder) => {
    setBusyId(order.id)
    try {
      const next = await retryWriteBack(project, order.id)
      if (next.written_back) toast.success('The record is now marked paid')
      else toast.error(next.writeback_error || 'Still could not stamp the record')
      await load()
    } catch (err) {
      toast.error(errText(err, 'Could not finish that order'))
    } finally {
      setBusyId('')
    }
  }

  if (loading && !data) {
    return <div style={{ padding: 30, marginLeft: 44 }}>Loading orders…</div>
  }

  const stuck = data?.summary.awaiting_writeback || 0

  return (
    <div style={{ marginLeft: 44 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
        {(data?.summary.by_currency || []).map(c => (
          <div key={c.currency} className="card" style={{ padding: 16, flex: '1 1 200px' }}>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Captured ({c.currency})
            </div>
            <div style={{ fontSize: 23, fontWeight: 600 }}>
              {money(c.captured, c.currency)}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
              {c.paid} paid · {c.failed} failed
              {c.refunded > 0 && <> · {money(c.refunded, c.currency)} refunded</>}
            </div>
          </div>
        ))}
        {!data?.summary.by_currency.length && (
          <div className="card" style={{ padding: 16, flex: 1, fontSize: 14,
                                         color: 'var(--color-text-secondary)' }}>
            No payment has been attempted on this application yet.
          </div>
        )}
      </div>

      {/* The one state that needs a human: money taken, record not stamped. */}
      {stuck > 0 && (
        <div className="card" style={{
          padding: 14, marginBottom: 16, display: 'flex', gap: 10,
          borderLeft: '3px solid var(--color-error, #dc2626)',
        }}>
          <AlertTriangle size={18} style={{ color: 'var(--color-error, #dc2626)', flexShrink: 0 }} />
          <div style={{ fontSize: 14 }}>
            <strong>{stuck} paid order{stuck === 1 ? '' : 's'} could not be
            marked paid in the application.</strong>{' '}
            <span style={{ color: 'var(--color-text-secondary)' }}>
              The money was taken. Use <em>Finish</em> on the rows below — the
              reason is shown there, and it is usually a validation or workflow
              rule blocking the update.
            </span>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0, marginBottom: 18, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--color-bg-secondary)', textAlign: 'left' }}>
              <th style={th}>Reference</th>
              <th style={th}>Record</th>
              <th style={th}>Payer</th>
              <th style={{ ...th, textAlign: 'right' }}>Amount</th>
              <th style={th}>Status</th>
              <th style={th}>When</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {(data?.orders || []).map(o => (
              <tr key={o.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={td}>{o.reference || '—'}</td>
                <td style={{ ...td, color: 'var(--color-text-secondary)' }}>{o.obj_name}</td>
                <td style={td}>{o.payer.name || o.payer.email || '—'}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {money(o.amount, o.currency)}
                  {o.amount_refunded > 0 && (
                    <div style={{ fontSize: 13, color: 'var(--color-warning, #d97706)' }}>
                      −{money(o.amount_refunded, o.currency)}
                    </div>
                  )}
                </td>
                <td style={{ ...td, color: STATUS_COLOR[o.status] }}>
                  {o.status.replace('_', ' ')}
                  {o.status === 'paid' && !o.written_back && (
                    <div style={{ fontSize: 13, color: 'var(--color-error, #dc2626)' }}
                         title={o.writeback_error}>
                      record not stamped
                    </div>
                  )}
                </td>
                <td style={{ ...td, color: 'var(--color-text-secondary)' }}>
                  {(o.paid_at || o.created_at || '').slice(0, 16).replace('T', ' ')}
                </td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {o.status === 'paid' && !o.written_back && (
                    <button className="btn btn-xs btn-ghost" disabled={busyId === o.id}
                            onClick={() => onRetry(o)}>
                      <RotateCcw size={12} /> Finish
                    </button>
                  )}
                  {(o.status === 'paid' || o.status === 'partially_refunded') && (
                    <button className="btn btn-xs btn-ghost" disabled={busyId === o.id}
                            onClick={() => onRefund(o)}>
                      Refund
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!data?.orders.length && (
              <tr><td style={{ ...td, color: 'var(--color-text-secondary)' }} colSpan={7}>
                Nothing yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* "Is my webhook arriving?" is the first question when an order is paid
          at the bank and unpaid here, so the answer is on this screen. */}
      <div className="card" style={{ padding: 18 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 10,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Recent gateway callbacks</div>
          <button className="btn btn-xs btn-ghost" onClick={() => void load()}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        {events.length === 0 ? (
          <p style={{ fontSize: 13.5, color: 'var(--color-text-secondary)', margin: 0 }}>
            No callback has arrived yet. If a customer has paid and the order
            still says unpaid, the webhook URL on the Setup tab is almost
            certainly not registered with the gateway.
          </p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 13.5 }}>
            {events.map(e => (
              <li key={e.id} style={{
                display: 'flex', gap: 10, padding: '5px 0',
                borderTop: '1px solid var(--color-border)',
              }}>
                <span style={{ color: 'var(--color-text-secondary)', minWidth: 130 }}>
                  {(e.created_at || '').slice(0, 16).replace('T', ' ')}
                </span>
                <span style={{ flex: 1 }}>{e.event_type}</span>
                <span style={{
                  color: e.status === 'failed'
                    ? 'var(--color-error, #dc2626)'
                    : 'var(--color-text-secondary)',
                }} title={e.error}>{e.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

const th: React.CSSProperties = { padding: '9px 12px', fontWeight: 600, fontSize: 13 }
const td: React.CSSProperties = { padding: '9px 12px', verticalAlign: 'top' }
